// Gate rendered in front of the portal on every demo visit.
//
// Order matters: the visitor signs in FIRST, then their sandbox is created,
// then their trial licence is checked, and only then does the portal load.
// Anonymous sign-in is disabled on the project, so crawlers and casual bounces
// no longer mint accounts or 300-document sandboxes. The portal bundle
// (~400 KB) is imported dynamically for the same reason — nobody downloads it
// until they are identified.
//
// The licence is checked BEFORE the sandbox is built. Seeding is ~300 writes
// against a Spark daily quota, and a visitor whose trial has lapsed would burn
// them on every return visit only to be shown the lock screen. The licence
// cache lives in the sandbox but reads and writes there work whether or not it
// has been seeded, so nothing is lost by asking first.

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { getApp, getApps, initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getFirestore, setDoc } from "firebase/firestore";
import { DEMO_LOGIN_ID, DEMO_NS, SANDBOX_TTL_DAYS } from "./demoConfig";
import { ensureSandboxSeeded } from "./seedClone";
import { recordVisitor } from "./demoUsers";
import { startSessionTracking } from "./sessionTracker";
import DemoAuthScreen from "./DemoAuthScreen";
import LicenseEndedScreen from "./TrialEndedScreen";
import { ensureLicense } from "./licenseService";
import { onUpgradeRequest, setDemoPlan } from "./planGate";
import { PLAN, planLabel } from "./plans";
import DemoUpgrade from "./DemoUpgrade";

const AppWrapper = lazy(() => import("../App"));

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Google is the only way in (see DemoAuthScreen), so a restored session has to
// be a Google one too — otherwise a leftover email/password session from before
// that path was removed would still walk straight past the front door.
const isUsable = (u) =>
  !!u &&
  !u.isAnonymous &&
  !!u.providerData?.some((p) => p.providerId === "google.com");

function Splash({ label, pct, error, onRetry }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 20, padding: 24,
        textAlign: "center", color: "#fff", fontFamily: "Inter, sans-serif",
        background:
          "radial-gradient(42rem 26rem at 78% -12%, rgba(255,130,87,.24), transparent 62%)," +
          "radial-gradient(40rem 26rem at 8% 108%, rgba(123,96,255,.46), transparent 62%)," +
          "linear-gradient(155deg,#120d24 0%,#1d1440 44%,#2f2069 100%)",
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>Staff Portal</div>
      {error ? (
        <>
          <p style={{ maxWidth: 420, opacity: 0.85, lineHeight: 1.6, fontSize: 15 }}>{error}</p>
          <button type="button" onClick={onRetry}
            style={{ padding: "12px 26px", borderRadius: 999, border: 0, background: "#ff8257",
                     color: "#17122e", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
            Try again
          </button>
        </>
      ) : (
        <>
          <p style={{ opacity: 0.8, fontSize: 15, margin: 0 }}>{label}</p>
          <div style={{ width: 260, maxWidth: "80vw", height: 4, borderRadius: 999,
                        background: "rgba(255,255,255,.16)", overflow: "hidden" }}>
            <div style={{ width: `${Math.round(pct * 100)}%`, height: "100%", borderRadius: 999,
                          background: "linear-gradient(90deg,#9b86ff,#ff8257)",
                          transition: "width .35s cubic-bezier(.23,1,.32,1)" }} />
          </div>
        </>
      )}
    </div>
  );
}

// The one piece of demo chrome over the portal: which tier you are on, how long
// is left, and — on the free month — the way to the full 3-month trial.
//
// On free it is always visible, because the upgrade is the whole point of the
// free tier. On the trial it stays out of the way until the last 30 days.
function PlanPill({ plan, daysLeft, onUpgrade }) {
  const free = plan === PLAN.FREE;
  if (!free && (daysLeft == null || daysLeft > 30)) return null;
  const urgent = daysLeft != null && daysLeft <= 7;

  const days =
    daysLeft == null
      ? null
      : daysLeft === 0
        ? "ends today"
        : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;

  return (
    <div
      style={{
        position: "fixed",
        right: 14,
        bottom: 14,
        zIndex: 9998,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: free ? "8px 8px 8px 15px" : "9px 15px",
        borderRadius: 999,
        fontFamily: "Inter, sans-serif",
        fontSize: 12.5,
        fontWeight: 700,
        color: "#fff",
        background: urgent
          ? "linear-gradient(90deg,#e2603a,#ff8257)"
          : "linear-gradient(90deg,#4a32bd,#6248e0)",
        boxShadow: "0 10px 24px -8px rgba(23,18,46,.75)",
      }}
    >
      <span style={{ opacity: 0.95 }}>
        {planLabel(plan)}
        {days ? ` · ${days}` : ""}
      </span>
      {free && (
        <button
          type="button"
          onClick={onUpgrade}
          style={{
            border: 0,
            cursor: "pointer",
            borderRadius: 999,
            padding: "6px 13px",
            fontSize: 12.5,
            fontWeight: 700,
            color: "#2b1c66",
            background: "#fff",
            whiteSpace: "nowrap",
          }}
        >
          Unlock all
        </button>
      )}
    </div>
  );
}

export default function DemoBoot() {
  const [phase, setPhase] = useState("checking"); // checking | auth | preparing | locked | ready
  const [pct, setPct] = useState(0.06);
  const [label, setLabel] = useState("Preparing your private demo…");
  const [error, setError] = useState(null);
  const [license, setLicense] = useState(null);
  const [visitor, setVisitor] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const preparing = useRef(false);

  const prepare = useCallback(async (user, isNewIdentity) => {
    if (preparing.current) return;
    preparing.current = true;
    setPhase("preparing");
    try {
      const db = getFirestore(app);

      setLabel("Checking your licence…");
      setPct(0.12);

      // Licence from the central License Server, bound to this visitor's uid:
      // a 1-month `free` tier on first sign-in, or the 3-month `demo` trial if
      // they have registered. Fails closed — anything we cannot verify locks
      // the portal rather than opening it. A server that is merely unreachable
      // is NOT a verdict; ensureLicense falls back to the cached signature's
      // 30-day grace.
      const verdict = await ensureLicense(db, user);
      setLicense(verdict);
      setVisitor(user);
      // Gate the portal's modules before it is imported, so nothing gated can
      // render for a frame on the wrong plan.
      setDemoPlan(verdict.plan, verdict.daysLeft);
      if (!verdict.ok) {
        // Deliberately leave `preparing` latched: onAuthStateChanged can fire
        // again and would otherwise re-run the whole sequence behind the lock
        // screen. Both buttons on that screen reload the page anyway.
        setPhase("locked");
        return;
      }

      setLabel("Setting up your hotel…");
      const { loginProfilePath } = await ensureSandboxSeeded(db, (done, total) =>
        setPct(0.18 + 0.72 * (done / total))
      );

      setPct(0.94);
      setLabel("Signing you in…");

      // Admin rights inside this visitor's OWN namespace only, so the Admin
      // Center is part of the tour.
      await setDoc(doc(db, `artifacts/${DEMO_NS}/public/data/adminRoles`, "roles"), {
        adminUids: [user.uid],
      }).catch(() => {});

      if (loginProfilePath) {
        const snap = await getDoc(doc(db, loginProfilePath));
        if (snap.exists()) {
          const profile = { id: snap.id, ...snap.data() };
          localStorage.setItem("loggedInUniqueId", DEMO_LOGIN_ID);
          localStorage.setItem("pp_user_doc_path", loginProfilePath);
          localStorage.setItem("pp_cached_profile", JSON.stringify(profile));
        }
      }

      await recordVisitor(db, user, isNewIdentity).catch(() => {});
      await setDoc(doc(db, "artifacts/demo-registry/public/data/sandboxes", DEMO_NS), {
        ns: DEMO_NS,
        uid: user.uid,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + SANDBOX_TTL_DAYS * 864e5).toISOString(),
      }).catch(() => {});

      startSessionTracking(db, user);

      setPct(1);
      setPhase("ready");
    } catch (err) {
      console.error("[demo] bootstrap failed:", err);
      setError("The demo could not be prepared. This is usually a temporary connection problem.");
      preparing.current = false;
    }
  }, []);

  // Locked menu items inside the portal call requestUpgrade(); this is what
  // they reach.
  useEffect(() => onUpgradeRequest(() => setShowUpgrade(true)), []);

  useEffect(() => {
    const auth = getAuth(app);
    return onAuthStateChanged(auth, (user) => {
      if (isUsable(user)) prepare(user, false);
      else setPhase((p) => (p === "preparing" || p === "ready" ? p : "auth"));
    });
  }, [prepare]);

  // The upgrade overlay must be reachable from the lock screen too — a lapsed
  // free month is exactly when someone decides to register.
  if (showUpgrade && visitor)
    return (
      <DemoUpgrade
        user={visitor}
        db={getFirestore(app)}
        daysLeft={license?.daysLeft}
        onClose={() => setShowUpgrade(false)}
      />
    );

  if (error) return <Splash error={error} onRetry={() => window.location.reload()} />;
  if (phase === "checking") return <Splash label="Loading…" pct={0.05} />;
  if (phase === "auth")
    return <DemoAuthScreen auth={getAuth(app)} onAuthenticated={prepare} />;
  if (phase === "preparing") return <Splash label={label} pct={pct} />;
  if (phase === "locked")
    return (
      <LicenseEndedScreen
        reason={license?.reason}
        plan={license?.plan}
        expiresAt={license?.expiresAt}
        onRetry={() => window.location.reload()}
        onUpgrade={visitor ? () => setShowUpgrade(true) : null}
      />
    );

  return (
    <Suspense fallback={<Splash label="Opening the portal…" pct={1} />}>
      <AppWrapper />
      <PlanPill
        plan={license?.plan}
        daysLeft={license?.daysLeft}
        onUpgrade={() => setShowUpgrade(true)}
      />
      {showUpgrade && visitor && (
        <DemoUpgrade
          user={visitor}
          db={getFirestore(app)}
          daysLeft={license?.daysLeft}
          onClose={() => setShowUpgrade(false)}
        />
      )}
    </Suspense>
  );
}
